// frontend/src/components/shop/ProductCard.js
import React, { useState } from 'react';
import { ShoppingCart, HelpCircle, ImageOff, X } from 'lucide-react';
import toast from 'react-hot-toast';

const ProductCard = ({ product, onAddToCart }) => {
  const [quantity, setQuantity] = useState(1);
  const [isAdding, setIsAdding] = useState(false);
  const [showDescription, setShowDescription] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);

  const resolvedImageUrl = product?.image_url
    ? (product.image_url.startsWith('//') ? `https:${product.image_url}` : product.image_url)
    : null;

  const handleAddToCart = async () => {
    if (quantity < 1) {
      toast.error('Количество должно быть больше 0');
      return;
    }
    
    setIsAdding(true);
    try {
      await onAddToCart(product.id, quantity);
      setQuantity(1);
    } catch (error) {
      console.error('Error adding to cart:', error);
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <>
      <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all overflow-hidden border border-gray-100 flex flex-col h-full">
        {/* Product Header */}
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-start justify-between mb-3">
            <h3 className="text-xl font-bold text-gray-900 line-clamp-2 flex-1 pr-2">
              {product.name}
            </h3>
            {product.description && (
              <button
                onClick={() => setShowDescription(true)}
                className="p-2 text-[#4897d2] hover:bg-blue-50 rounded-full transition-colors flex-shrink-0"
                title="Для чего это?"
              >
                <HelpCircle className="w-5 h-5" />
              </button>
            )}
          </div>

          {product.pack_quantity && (
            <div className="inline-block bg-blue-50 text-blue-700 text-xs font-semibold px-3 py-1 rounded-full mb-3">
              {product.pack_quantity} шт. в упаковке
            </div>
          )}
        </div>

        {/* Product Body */}
        <div className="p-6 flex-1 flex flex-col">
          {resolvedImageUrl && !imageFailed ? (
            <div className="mb-4 rounded-xl overflow-hidden border border-gray-100 bg-gray-50">
              <img
                src={resolvedImageUrl}
                alt={product.name}
                className="w-full h-44 object-cover"
                loading="lazy"
                onError={() => setImageFailed(true)}
              />
            </div>
          ) : (
            <div className="mb-4 rounded-xl border border-dashed border-gray-200 bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50 p-5 text-center">
              <ImageOff className="w-5 h-5 text-slate-400 mx-auto mb-2" />
              <p className="text-sm font-semibold text-slate-600">Картинка будет добавлена</p>
            </div>
          )}

          {/* Category Badge */}
          {product.category && (
            <div className="mb-4">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                {product.category.name}
              </span>
            </div>
          )}

          {/* Price */}
          <div className="mt-auto mb-6">
            <div className="flex items-baseline gap-2 mb-2">
              <span className="text-3xl font-bold text-[#4897d2]">
                {product.price}₪
              </span>
              {product.is_available ? (
                <span className="text-xs text-green-600 font-medium bg-green-50 px-2 py-1 rounded">
                  В наличии
                </span>
              ) : (
                <span className="text-xs text-red-600 font-medium bg-red-50 px-2 py-1 rounded">
                  Нет в наличии
                </span>
              )}
            </div>
          </div>

          {/* Quantity and Add to Cart */}
          <div className="space-y-3">
            <div className="flex items-center justify-center border-2 border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                className="px-4 py-2.5 text-gray-600 hover:bg-gray-50 transition-colors font-semibold disabled:opacity-50"
                disabled={isAdding || quantity <= 1}
              >
                −
              </button>
              <input
                type="number"
                min="1"
                value={quantity}
                onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                className="w-16 text-center border-0 focus:ring-0 p-0 font-semibold text-gray-900"
                disabled={isAdding}
              />
              <button
                onClick={() => setQuantity(quantity + 1)}
                className="px-4 py-2.5 text-gray-600 hover:bg-gray-50 transition-colors font-semibold disabled:opacity-50"
                disabled={isAdding}
              >
                +
              </button>
            </div>
            
            <button
              onClick={handleAddToCart}
              disabled={isAdding || !product.is_available}
              className="w-full bg-gradient-to-r from-[#662d91] via-[#4897d2] to-[#28c4f4] text-white px-4 py-3 rounded-lg font-semibold hover:opacity-90 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
            >
              {isAdding ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                  <span>Добавление...</span>
                </>
              ) : (
                <>
                  <ShoppingCart className="w-5 h-5" />
                  <span>В корзину</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Description Modal */}
      {showDescription && product.description && (
        <div 
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setShowDescription(false)}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between z-10">
              <h2 className="text-2xl font-bold text-gray-900">
                {product.name}
              </h2>
              <button
                onClick={() => setShowDescription(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <HelpCircle className="w-5 h-5 text-[#4897d2]" />
                  Для чего это?
                </h3>
                <div className="prose prose-sm max-w-none">
                  <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                    {product.description}
                  </p>
                </div>
              </div>

              {/* Product Info */}
              <div className="mt-6 pt-6 border-t border-gray-200 space-y-3">
                {product.category && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Категория:</span>
                    <span className="font-semibold text-gray-900">{product.category.name}</span>
                  </div>
                )}
                {product.pack_quantity && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">В упаковке:</span>
                    <span className="font-semibold text-gray-900">{product.pack_quantity} шт.</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600">Цена:</span>
                  <span className="font-bold text-[#4897d2] text-xl">{product.price}₪</span>
                </div>
              </div>

              {/* Add to Cart in Modal */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex items-center border-2 border-gray-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="px-3 py-2 text-gray-600 hover:bg-gray-50 transition-colors"
                      disabled={quantity <= 1}
                    >
                      −
                    </button>
                    <input
                      type="number"
                      min="1"
                      value={quantity}
                      onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                      className="w-12 text-center border-0 focus:ring-0 p-0 font-semibold"
                    />
                    <button
                      onClick={() => setQuantity(quantity + 1)}
                      className="px-3 py-2 text-gray-600 hover:bg-gray-50 transition-colors"
                    >
                      +
                    </button>
                  </div>
                  <button
                    onClick={() => {
                      handleAddToCart();
                      setShowDescription(false);
                    }}
                    disabled={isAdding || !product.is_available}
                    className="flex-1 bg-gradient-to-r from-[#662d91] via-[#4897d2] to-[#28c4f4] text-white px-4 py-2 rounded-lg font-semibold hover:opacity-90 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {isAdding ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                    ) : (
                      <>
                        <ShoppingCart className="w-5 h-5" />
                        <span>В корзину</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProductCard;
